import { IAuthor } from '../../../../shared/models/author.model';

/** Situação do lote. */
export enum UploadDocumentoStatus {
    Criado = 0,
    EmProcessamento = 1,
    Processado = 2,
    Erro = 3,
}

/** Situação de assinatura do documento gerado (espelha ArquivosTrabalhadoresAssinaturaEnum). */
export enum UploadDocumentoItemAssinatura {
    NaoSolicitada = 0,
    Pendente = 1,
    Assinado = 2,
    Rejeitado = 3,
    Cancelado = 4,
}

/** Situação de reversão; ausente significa sem reversão. */
export enum UploadDocumentoStatusReversao {
    Parcial = 1,
    Total = 2,
    /** Transitório: a reversão está na fila ou rodando no worker. */
    EmAndamento = 3,
}

/** Situação de cada item do lote (um por colaborador). */
export enum UploadDocumentoItemStatus {
    Pendente = 0,
    Sucesso = 1,
    Erro = 2,
    Revertido = 3,
}

/** Tipo de operação no histórico do lote. */
export enum UploadDocumentoHistoricoTipo {
    Upload = 0,
    ReversaoParcial = 1,
    ReversaoTotal = 2,
}

export interface IUploadDocumentoTipoArquivo {
    tipoarquivo: string;
    codigo: string;
    titulo: string;
    exigeliberacao?: boolean;
    assinatura?: boolean;
}

export interface IUploadDocumentoEmpresa {
    empresa: string;
    codigo: string;
    razaosocial: string;
}

export interface IUploadDocumentoEstabelecimento {
    estabelecimento: string;
    codigo: string;
    nomefantasia: string;
}

export interface IUploadDocumentoDepartamento {
    departamento: string;
    codigo: string;
    nome: string;
}

export interface IUploadDocumentoLotacao {
    lotacao: string;
    codigo: string;
    nome: string;
}

export interface IUploadDocumentoErro {
    codigo: string;
    mensagem: string;
    detalhe: string;
    datahora: string;
}

export interface IUploadDocumentoLog {
    auditoria?: {
        operacao: string;
        executado_por: IAuthor;
        executado_em: string;
        origem: string;
    };
    fila?: {
        message: string;
        receiver: string;
        status: string;
    };
    contexto?: {
        tipoarquivo?: string;
        tipoarquivo_codigo?: string;
        nome?: string;
        competenciaano?: number | null;
        competenciames?: number | null;
        empresa?: string;
        estabelecimento?: string | null;
        departamento_colaboradores?: string | null;
        lotacao_colaboradores?: string | null;
        qtd_trabalhadores_informados?: number;
    };
    resumo?: {
        qtd_colaboradores?: number;
        qtd_documentos_gerados?: number;
        qtd_erros?: number;
        qtd_substituicoes?: number;
        qtd_documentos_revertidos?: number;
        qtd_falhas?: number;
        qtd_documentos_restantes?: number;
    };
    erro?: IUploadDocumentoErro;
    falhas?: {
        trabalhador: string;
        nome: string;
        motivo: string;
    }[];
    colaboradores_afetados?: {
        trabalhador: string;
        nome: string;
        codigo: string;
    }[];
}

/** Item da listagem de lotes. */
export interface IUploadDocumentoListItem {
    uploaddocumento: string;
    tenant: number;
    nome: string;
    competenciaano: number | null;
    competenciames: number | null;
    status: UploadDocumentoStatus;
    statusreversao: UploadDocumentoStatusReversao | null;
    created_at: string;
    created_by: IAuthor;
    lastupdate: string;
    tipoarquivo_id: string;
    tipoarquivo_codigo: string;
    tipoarquivo_titulo: string;
    qtd_colaboradores: number;
}

/** Detalhe do lote. */
export interface IUploadDocumentoGet {
    uploaddocumento: string;
    tenant: number;
    nome: string;
    patharquivo: string;
    extensao: string;
    competenciaano: number | null;
    competenciames: number | null;
    status: UploadDocumentoStatus;
    statusreversao: UploadDocumentoStatusReversao | null;
    log: IUploadDocumentoLog;
    erro: IUploadDocumentoErro | null;
    created_at: string;
    created_by: IAuthor;
    updated_at: string | null;
    updated_by: IAuthor | null;
    lastupdate: string;
    tipoarquivo: IUploadDocumentoTipoArquivo;
    empresa: IUploadDocumentoEmpresa;
    estabelecimento: IUploadDocumentoEstabelecimento | null;
    departamento: IUploadDocumentoDepartamento | null;
    lotacao: IUploadDocumentoLotacao | null;
    pode_reverter: boolean;
}

/**
 * Um item por colaborador.
 *
 * `arquivotrabalhador` é o vínculo com o documento gerado: o lote aponta para o
 * documento, e não o contrário. `documento_excluido_externamente` marca documento
 * excluído fora do lote, que fica visível em vez de desaparecer da tela; item
 * revertido pelo próprio lote não recebe a marca.
 */
export interface IUploadDocumentoItem {
    uploaddocumentoitem: string;
    uploaddocumento: string;
    trabalhador: string;
    arquivotrabalhador: string | null;
    status: UploadDocumentoItemStatus;
    mensagem: string | null;
    created_at: string;
    codigo: string;
    nome: string;
    estabelecimento: string;
    situacaoliberacao: boolean | null;
    situacaoassinatura: number | null;
    documento_excluido_externamente: boolean;
}

export interface IUploadDocumentoHistorico {
    uploaddocumentohistorico: string;
    uploaddocumento: string;
    tenant: number;
    tipo: UploadDocumentoHistoricoTipo;
    log: IUploadDocumentoLog;
    erro: IUploadDocumentoErro | null;
    created_at: string;
    created_by: IAuthor;
    lastupdate: string;
}

export interface IUploadDocumentoTrabalhadorPayload {
    trabalhador: string;
}

/**
 * Item que a reversão pode excluir: enviado, com documento existente, não
 * excluído fora do lote e não assinado.
 *
 * Mesmo critério do `buscarItensParaReversao` no backend. Vive aqui porque a tela
 * de detalhe e a tabela de colaboradores precisam da mesma resposta — uma para
 * oferecer a ação, a outra para habilitar a seleção.
 */
export function isItemElegivelParaReversao(item: IUploadDocumentoItem): boolean {
    return item.status === UploadDocumentoItemStatus.Sucesso
        && !!item.arquivotrabalhador
        && !item.documento_excluido_externamente
        && item.situacaoassinatura !== UploadDocumentoItemAssinatura.Assinado;
}

/** Corpo da prévia e da criação. */
export interface IUploadDocumentoCreate {
    tipoarquivo_codigo: string;
    nome: string;
    /** ausente na prévia: só a criação grava o arquivo. */
    conteudo?: string;
    competenciaano: number | null;
    competenciames: number | null;
    empresa: string;
    estabelecimento: string;
    departamento_colaboradores: string | null;
    lotacao_colaboradores: string | null;
    trabalhadores: IUploadDocumentoTrabalhadorPayload[];
}

export interface IUploadDocumentoPreviewTrabalhador {
    trabalhador: string;
    codigo: string;
    nome: string;
    elegivel: boolean;
    motivo: string | null;
    substitui_documento_anterior: boolean;
}

export interface IUploadDocumentoPreview {
    qtd_trabalhadores_afetados: number;
    qtd_documentos_previstos: number;
    qtd_ignorados: number;
    qtd_ignorados_documento_liberado: number;
    qtd_substituicoes: number;
    tipoarquivo: {
        tipoarquivo: string;
        codigo: string;
        titulo: string;
    };
    trabalhadores: IUploadDocumentoPreviewTrabalhador[];
}

export interface IUploadDocumentoReversao {
    trabalhadores?: IUploadDocumentoTrabalhadorPayload[];
}

export interface IUploadDocumentoConstructor {}

export interface IUploadDocumentoOffset {
    uploaddocumento?: string;
}
